import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, formatApiError } from "@/lib/apiClient";
import { QuestionView, ReviewCard } from "@/components/QuestionView";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Timer, Send, Lock } from "lucide-react";

export default function PremiumPractice() {
  const { category } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [locked, setLocked] = useState(false);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [remaining, setRemaining] = useState(0);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get(`/practice/${category}/premium`)
      .then((r) => { setData(r.data); setRemaining(r.data.duration_minutes * 60); })
      .catch((e) => { if (e.response?.status === 402) setLocked(true); else { toast.error(formatApiError(e.response?.data?.detail)); nav("/tests"); } });
  }, [category, nav]);

  useEffect(() => {
    if (!data || remaining <= 0 || result) return;
    const t = setInterval(() => setRemaining((r) => r - 1), 1000);
    return () => clearInterval(t);
  }, [data, remaining, result]);

  const submit = async () => {
    const payload = { category, mode: "premium", answers: data.questions.map((q) => ({ question_id: q.id, answer: answers[q.id] || null })) };
    try { const { data: res } = await api.post("/practice/submit", payload); setResult(res); window.scrollTo(0, 0); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (locked) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-6 py-20 text-center">
          <Lock className="w-12 h-12 text-[#002FA7] mx-auto mb-6" strokeWidth={1.5} />
          <h1 className="font-display font-black text-4xl tracking-tighter mb-4">Nessuna domanda sbloccata</h1>
          <p className="text-slate-600 mb-8">Sblocca tutta la banca da 450 domande con la formula «Pro» o «Superiore».</p>
          <Link to="/pricing"><Button data-testid="premium-locked-pricing" className="rounded-none bg-[#002FA7] hover:bg-[#001d6b] label-mono py-6 px-8">Vai ai prezzi</Button></Link>
        </div>
      </Layout>
    );
  }
  if (!data) return <Layout><div className="max-w-5xl mx-auto p-8 text-slate-500">Caricamento…</div></Layout>;

  if (result) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-12">
          <div className="border border-slate-200 bg-white p-10 mb-8 shadow-hard-black" data-testid="premium-result-score">
            <div className="label-mono text-[#002FA7] mb-2">— Punteggio Premium</div>
            <h1 className="font-display font-black text-6xl tracking-tighter">{result.score_percent}%</h1>
            <p className="text-slate-600 mt-4">{result.correct} corrette su {result.total} · {data.title}</p>
          </div>
          <h2 className="font-display font-bold text-2xl mb-6">Revisione</h2>
          <div className="space-y-4">{result.details.map((d, i) => <ReviewCard key={d.question_id} d={d} index={i} />)}</div>
          <div className="mt-8 flex gap-4">
            <Link to="/tests"><Button className="rounded-none bg-[#002FA7] hover:bg-[#001d6b] label-mono py-6">Torna ai test</Button></Link>
          </div>
        </div>
      </Layout>
    );
  }

  const q = data.questions[idx];
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8">
        <div className="border border-slate-200 bg-white flex items-center justify-between p-4 mb-6">
          <div>
            <div className="label-mono text-slate-500">{data.title}</div>
            <div className="font-display font-bold text-lg">Domanda {idx + 1} di {data.questions.length}</div>
          </div>
          <div className={`flex items-center gap-2 px-4 py-2 border ${remaining < 60 ? "border-red-500 text-red-600" : "border-slate-300"}`}>
            <Timer className="w-4 h-4" /><span className="font-mono-num text-lg font-bold">{mm}:{ss}</span>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3 border border-slate-200 bg-white p-8">
            <QuestionView q={q} value={answers[q.id]} onChange={(k) => setAnswers({ ...answers, [q.id]: k })} />
            <div className="flex justify-between mt-8 pt-6 border-t border-slate-200">
              <Button variant="outline" className="rounded-none label-mono" disabled={idx === 0} onClick={() => setIdx(idx - 1)} data-testid="premium-prev">Precedente</Button>
              {idx < data.questions.length - 1
                ? <Button className="rounded-none bg-[#002FA7] hover:bg-[#001d6b] label-mono" onClick={() => setIdx(idx + 1)} data-testid="premium-next">Successiva</Button>
                : <Button className="rounded-none bg-[#FFD700] hover:bg-yellow-300 text-[#0A0F1A] label-mono" onClick={submit} data-testid="premium-submit"><Send className="w-4 h-4 mr-2" />Termina e correggi</Button>}
            </div>
          </div>
          <div className="lg:col-span-1 border border-slate-200 bg-white p-4 h-fit sticky top-20">
            <div className="label-mono text-slate-500 mb-3">Domande</div>
            <div className="grid grid-cols-5 gap-2">
              {data.questions.map((qq, i) => (
                <button key={qq.id} onClick={() => setIdx(i)} data-testid={`premium-goto-${i}`}
                  className={`aspect-square text-sm font-mono-num border ${i === idx ? "border-[#002FA7] bg-[#002FA7] text-white" : answers[qq.id] ? "border-[#002FA7] text-[#002FA7]" : "border-slate-200 text-slate-500"}`}>{i + 1}</button>
              ))}
            </div>
            <Button className="w-full mt-4 rounded-none bg-[#0A0F1A] hover:bg-slate-800 label-mono" onClick={submit} data-testid="premium-submit-side">Termina e correggi</Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
